import { DndContext, DragOverlay, MeasuringStrategy, useDroppable } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { AnimatePresence, motion } from "framer-motion";
import { Fragment, memo, useState } from "react";
import SVG from "react-inlinesvg";
import { SudoButton } from "src/sudokit/components/SudoButton";
import styled from "styled-components/macro";
import { useStructuredOutlineStore } from "../StructuredOutlineStore";
import Act from "./Act";
import Chapter from "./Chapter";

const OutlineBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DroppableArea = ({ itemId, readOnly, isLast, isEmptyOutline }) => {
  const { attributes, setNodeRef, isOver } = useDroppable({ id: itemId, disabled: readOnly });
  const [isHovered, setIsHovered] = useState(false);
  const addChapter = useStructuredOutlineStore((state) => state.addChapter);
  const addBlankAct = useStructuredOutlineStore((state) => state.addBlankAct);
  const isStart = itemId === "start";
  const showActButton = isStart || isLast;
  const addAfterId = isStart ? "beginning" : isLast ? "end" : itemId;

  if (isStart) {
    return (
      <div
        ref={setNodeRef}
        {...attributes}
        style={{
          height: "6px",
          borderRadius: "4px",
          backgroundColor: isOver ? "var(--primary-cta)" : "transparent",
          margin: "4px",
        }}
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      className="relative flex items-center justify-center overflow-hidden"
      style={{
        background: isOver
          ? `linear-gradient(to bottom, transparent calc(50% - 1.5px), var(--primary-cta) calc(50% - 1.5px), var(--primary-cta) calc(50% + 1.5px), transparent calc(50% + 1.5px))`
          : "transparent",
        minHeight: "20px",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <AnimatePresence>
        {isHovered && !isOver && !isEmptyOutline && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: "easeInOut" },
              opacity: { duration: 0.75, ease: "easeInOut" },
            }}
            className="flex gap-2 justify-center"
            style={{ overflow: "hidden" }}
          >
            <SudoButton
              variant="tertiary"
              className="mt-2 mb-2 pt-0 pb-0 bg-[var(--menu-color)] hover:bg-[var(--menu-color)]"
              onClick={() => addChapter(addAfterId)}
              leftIcon="/assets/add.svg"
            >
              Add Chapter
            </SudoButton>
            {showActButton && (
              <SudoButton
                variant="tertiary"
                className="mt-2 mb-2 pt-0 pb-0 bg-[var(--menu-color)] hover:bg-[var(--menu-color)]"
                onClick={() => addBlankAct(addAfterId)}
                leftIcon="/assets/add.svg"
              >
                Add Act
              </SudoButton>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function DraggingOverlay({ draggingItemId, items }) {
  if (!draggingItemId) {
    return null;
  }
  const draggedItem = items.find((item) => item.id === draggingItemId);
  if (!draggedItem) {
    return null;
  }
  const isAct = draggedItem.type === "act";

  const styles = {
    backgroundColor: isAct ? "transparent" : "var(--menu-color-fullscreen)",
    cursor: "grabbing",
    display: "flex",
    alignItems: "center",
    padding: "8px",
    borderRadius: "22px",
    opacity: 0.8,
  };
  return (
    <div style={styles}>
      <SVG
        src="/assets/grabber.svg"
        height={24}
        width={24}
      />
      {draggedItem.name}
    </div>
  );
}

function OutlineBody({ projectId, outlineProjectId, readOnly, outline, reorderItem, syncing, onFieldBlur }) {
  const [draggingItemId, setDraggingItemId] = useState(null);
  const items = outline?.content?.items || [];
  const isEmptyOuline = items.length === 0;
  const firstChapter = items.find((item) => item.type === "chapter");
  const firstChapterId = firstChapter?.id;

  return (
    <OutlineBodyContainer id="outline-body-container">
      <DndContext
        onDragStart={({ active }) => {
          if (!active) {
            return;
          }
          setDraggingItemId(active.id);
        }}
        onDragEnd={({ over }) => {
          setDraggingItemId(null);
          if (over) {
            reorderItem(draggingItemId, over.id);
          }
        }}
        onDragCancel={() => {
          setDraggingItemId(null);
        }}
        modifiers={[restrictToVerticalAxis]}
        layoutMeasuring={{ strategy: MeasuringStrategy.Always }}
      >
        <DroppableArea
          itemId="start"
          readOnly={readOnly}
          isEmptyOutline={isEmptyOuline}
        />
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          if (item.type === "act") {
            return (
              <Fragment key={item.id}>
                <Act
                  act={item}
                  draggingItemId={draggingItemId}
                  readOnly={readOnly}
                  syncing={syncing}
                />
                <DroppableArea
                  itemId={item.id}
                  readOnly={readOnly}
                  isLast={isLast}
                  isEmptyOutline={isEmptyOuline}
                />
              </Fragment>
            );
          }
          if (item.type === "chapter") {
            return (
              <Fragment key={item.id}>
                <Chapter
                  chapter={item}
                  draggingItemId={draggingItemId}
                  readOnly={readOnly}
                  projectId={projectId}
                  outlineProjectId={outlineProjectId}
                  isFirstChapter={item.id === firstChapterId}
                  syncing={syncing}
                  onSummaryBlur={onFieldBlur}
                />
                <DroppableArea
                  itemId={item.id}
                  readOnly={readOnly}
                  isLast={isLast}
                  isEmptyOutline={isEmptyOuline}
                />
              </Fragment>
            );
          }
          return <div key={`item-${index}`}>Unknown Item Type: {item.type}</div>;
        })}

        <DragOverlay>
          <DraggingOverlay
            draggingItemId={draggingItemId}
            items={items}
          />
        </DragOverlay>
      </DndContext>
    </OutlineBodyContainer>
  );
}

export default memo(OutlineBody);
